import { useEffect, useRef, useState } from 'react';
import anchorme from 'anchorme';
import { escapeHtml, getResizedImage } from 'shared-helpers/common';
import { useReduxState } from 'hooks';
import { getAllLanguageOptionsByLangCode } from 'lib/courses';
import { newlinesToLineBreak, renderTextWithEmoji } from 'lib/helpers';
import { FlagIcon, MoreOptions, User } from 'components/common';
import { DropdownButton } from 'components/common/dropdown';
import { Icon } from 'components/common/icons';
import { EditedContent } from 'components/common/notifications';
import {
  ReaderBlockDiscussionCommentDelete,
  ReaderBlockDiscussionCommentReactions
} from '.';

import type { CommonBlockPropsReader } from 'components/reader/blocks';
import type { DiscussionComment } from './ReaderBlockDiscussion';

interface Props
  extends Pick<CommonBlockPropsReader<'discussion'>, 'font' | 'fontSize'> {
  comment: DiscussionComment;
  onDelete: (
    commentId: number,
    parentCommentId?: number,
    deletionReason?: string
  ) => Promise<void>;
  onEdit: () => void;
}

const ReaderBlockDiscussionComment = ({
  comment: {
    attachment,
    attachmentType,
    courseLanguageVersion,
    createdAt,
    updatedAt,
    id,
    parentCommentId,
    reactions,
    text,
    user: commentUser
  },
  font,
  fontSize,
  onDelete,
  onEdit
}: Props) => {
  const {
    messages,
    pageState: { readerCourseData },
    serverConfig: { discussionEnableReactions },
    theme,
    user
  } = useReduxState();

  const image = useRef<HTMLImageElement>(undefined);

  const [imageLoaded, setImageLoaded] = useState(false);

  const emojiSize = { small: 16, medium: 18, large: 20, huge: 22 };

  useEffect(() => {
    return () => {
      clearTimeout(waitForResizedImageTimeout);
    };
  }, []);

  let numberOfReloads = 0;
  let waitForResizedImageTimeout: NodeJS.Timeout;
  const handleImageError = () => {
    if (numberOfReloads < 5) {
      numberOfReloads++;

      waitForResizedImageTimeout = setTimeout(() => {
        const resizedUrl = getResizedImage({ url: attachment, size: 640 });
        if (image.current) image.current.src = resizedUrl;
      }, 1000);
    } else setImageLoaded(true);
  };

  const renderContent = (text: string) => {
    const anchoredText = anchorme({
      input: newlinesToLineBreak(escapeHtml(text)),
      options: { attributes: { target: '_blank', rel: 'noopener noreferrer' } }
    });

    return renderTextWithEmoji(anchoredText, emojiSize[fontSize]).map(
      (p, i) => (
        <span key={i} dangerouslySetInnerHTML={{ __html: p as string }} />
      )
    );
  };

  const languageOptions = getAllLanguageOptionsByLangCode();

  const isPrivilegedUser =
    readerCourseData?.isCourseOwner ||
    readerCourseData?.isCourseAdmin ||
    readerCourseData?.isSuperAdmin;

  return (
    <div className="comment">
      <div className="header">
        <div className="info">
          <User
            anonymous={!commentUser}
            enableProfileLink={isPrivilegedUser}
            user={commentUser}
            size={
              parentCommentId
                ? ({
                    small: 'small',
                    medium: 'small',
                    large: 'medium',
                    huge: 'large'
                  }[fontSize] as 'small' | 'medium' | 'large' | 'huge')
                : fontSize
            }
            subtitle={
              <EditedContent
                createdAt={createdAt as string}
                updatedAt={updatedAt as string}
              />
            }
          />
          {courseLanguageVersion &&
            Object?.keys(courseLanguageVersion)?.length > 0 && (
              <FlagIcon
                lang={courseLanguageVersion}
                style={{ marginLeft: 10 }}
                tooltipText={
                  messages.courseLanguageVersion +
                  ': ' +
                  languageOptions[courseLanguageVersion]?.capitalizedName
                }
              />
            )}
        </div>

        {isPrivilegedUser || (commentUser && user?.id === commentUser.id) ? (
          <div className="dropdown">
            <MoreOptions
              category={parentCommentId ? 'lighter' : 'transparent'}
              styleContentButtons={false}
            >
              {commentUser && user?.id === commentUser.id ? (
                <DropdownButton
                  icon="pen"
                  onClick={onEdit}
                  text={messages.commonEdit}
                />
              ) : null}

              {isPrivilegedUser ? (
                <ReaderBlockDiscussionCommentDelete
                  commentId={id}
                  parentCommentId={parentCommentId}
                  onDelete={onDelete}
                />
              ) : null}
            </MoreOptions>
          </div>
        ) : null}
      </div>

      <div
        className="text"
        style={{
          fontFamily: font,
          fontSize: {
            small: '1.4rem',
            medium: '1.5rem',
            large: '1.6rem',
            huge: '1.8rem'
          }[fontSize]
        }}
      >
        {renderContent(text)}
      </div>

      {attachment ? (
        <div className="attachment">
          {attachmentType === 'image' ? (
            <a
              className="attachment-image-link"
              href={attachment}
              rel="noopener noreferrer"
              target="_blank"
            >
              {!imageLoaded ? (
                <div className="attachment-link" style={{ fontSize: '1.4rem' }}>
                  {messages.readerDiscussionCommentImageLoading}
                </div>
              ) : null}

              <img
                alt={attachment.slice(attachment.lastIndexOf('/') + 1)}
                className="attachment-image"
                onError={handleImageError}
                onLoad={() => setImageLoaded(true)}
                ref={image}
                sizes="100vw"
                src={getResizedImage({ url: attachment, size: 640 })}
                srcSet={
                  `${getResizedImage({
                    url: attachment,
                    size: 1920
                  })} 1920w,` +
                  `${getResizedImage({
                    url: attachment,
                    size: 1280
                  })} 1280w,` +
                  `${getResizedImage({
                    url: attachment,
                    size: 960
                  })} 960w,` +
                  `${getResizedImage({
                    url: attachment,
                    size: 640
                  })} 640w,` +
                  `${getResizedImage({ url: attachment, size: 480 })} 480w`
                }
                style={imageLoaded ? {} : { display: 'none' }}
              />
            </a>
          ) : (
            <a
              className="attachment-link"
              href={attachment}
              rel="noopener noreferrer"
              target="_blank"
              style={{
                fontSize: {
                  small: '1.3rem',
                  medium: '1.4rem',
                  large: '1.5rem',
                  huge: '1.6rem'
                }[fontSize]
              }}
            >
              <div className="attachment-link-icon">
                <Icon icon={['far', 'file']} />
              </div>

              <div className="attachment-link-text">
                {attachment.slice(attachment.lastIndexOf('/') + 1)}
              </div>
            </a>
          )}
        </div>
      ) : null}

      {discussionEnableReactions ? (
        <div style={!attachment ? { marginTop: 10 } : {}}>
          <ReaderBlockDiscussionCommentReactions
            commentId={id}
            commentReactions={reactions}
            isChildComment={parentCommentId ? true : false}
          />
        </div>
      ) : null}

      <style jsx>{`
        .comment {
          font-size: 1.6rem;
        }
        .comment :global(.emoji-mart-emoji) {
          vertical-align: text-bottom;
        }
        .header {
          align-items: flex-start;
          display: flex;
          flex-direction: row;
          margin-bottom: 10px;
        }
        .info {
          flex-grow: 1;
          flex-shrink: 1;
        }
        .dropdown {
          flex-grow: 0;
          flex-shrink: 0;
          margin-left: 15px;
          text-align: right;
        }
        .text {
          color: ${theme.textColor};
          font-size: 1.4rem;
          word-break: break-word;
        }
        .attachment {
          margin: 20px 0 5px;
        }
        .attachment-image-link {
          color: inherit;
          display: inline-block;
          margin-right: 10px;
          max-width: 440px;
          text-decoration: none;
        }
        .attachment-image {
          max-width: 100%;
        }
        .attachment-link {
          background-color: ${parentCommentId
            ? theme.lightGreyDark
            : theme.lightGrey};
          border-radius: 5px;
          color: inherit;
          display: inline-flex;
          font-weight: 600;
          max-width: 100%;
          padding: 4px 10px;
          text-decoration: none;
          word-break: break-word;
        }
        .attachment-link:focus,
        .attachment-link:hover {
          text-decoration: underline;
        }
        .attachment-link-icon {
          margin-right: 8px;
        }
        .attachment-link-text {
          text-decoration: inherit;
        }
      `}</style>
    </div>
  );
};

export default ReaderBlockDiscussionComment;
